import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ArrowLeft, Link2, Loader2, CheckCircle, AlertCircle, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportEvent = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, isBusinessPremium } = useAuth();
  const [url, setUrl] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [importing, setImporting] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  if (!isBusinessPremium()) {
    return (
      <div className="min-h-screen py-12">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <Globe className="w-16 h-16 text-gold mx-auto mb-6" />
          <h1 className="text-3xl mb-4">Import External Events</h1>
          <p className="text-muted-foreground mb-8">
            This feature is available for Business Premium members only.
            Upgrade to import events from LinkedIn, Eventbrite, Meetup, and more.
          </p>
          <Button 
            onClick={() => navigate('/membership')}
            className="bg-gold text-primary hover:bg-gold/90"
          >
            Upgrade to Business Premium
          </Button>
        </div>
      </div>
    );
  }

  const handleScrape = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setLoading(true);
    setExtractedData(null);

    try {
      const response = await axios.post(
        `${API}/events/scrape-url`,
        { url: url.trim(), source_name: sourceName || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        toast.success('Event details extracted successfully!');
      } else {
        toast.error('Could not extract event details');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error scraping URL');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!extractedData) return;

    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/events/import`,
        { events: [extractedData] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.imported.length > 0) {
        toast.success('Event imported successfully!');
        navigate(`/events/${response.data.imported[0].id}`);
      } else {
        toast.error('Failed to import event');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importing event');
    } finally {
      setImporting(false);
    }
  };

  const updateExtractedField = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10">
          <p className="overline text-gold mb-4">Import</p>
          <h1 className="text-3xl md:text-4xl">Import External Event</h1>
          <p className="text-muted-foreground mt-2">
            Paste a URL from LinkedIn, Eventbrite, Meetup, or any event page
          </p>
        </div>

        {/* URL Input Section */}
        <div className="border border-border/50 p-6 bg-card mb-8">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Event URL *</Label>
              <div className="flex gap-2">
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.linkedin.com/events/..."
                  className="flex-1 rounded-sm border-border/50"
                  data-testid="import-url-input"
                />
                <Button
                  onClick={handleScrape}
                  disabled={loading || !url.trim()}
                  className="bg-gold text-primary hover:bg-gold/90"
                  data-testid="scrape-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                  <span className="ml-2">{loading ? 'Extracting...' : 'Extract'}</span>
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Source (optional)</Label>
              <Select value={sourceName} onValueChange={setSourceName}>
                <SelectTrigger className="rounded-sm border-border/50">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Auto-detect</SelectItem>
                  <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                  <SelectItem value="Eventbrite">Eventbrite</SelectItem>
                  <SelectItem value="Meetup">Meetup</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Extracted Data Preview/Edit */}
        {extractedData && (
          <div className="border border-gold/50 p-6 bg-gold/5 rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold">Extracted Event Details</h3>
              <span className="text-xs text-muted-foreground ml-auto">
                Review and edit before importing
              </span>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Title</Label>
                <Input
                  value={extractedData.title || ''}
                  onChange={(e) => updateExtractedField('title', e.target.value)}
                  className="rounded-sm border-border/50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Description</Label>
                <Textarea
                  value={extractedData.description || ''}
                  onChange={(e) => updateExtractedField('description', e.target.value)}
                  rows={4}
                  className="rounded-sm border-border/50"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Date</Label>
                  <Input
                    type="date"
                    value={extractedData.date || ''}
                    onChange={(e) => updateExtractedField('date', e.target.value)}
                    className="rounded-sm border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Start Time</Label>
                  <Input
                    type="time"
                    value={extractedData.start_time || ''}
                    onChange={(e) => updateExtractedField('start_time', e.target.value)}
                    className="rounded-sm border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">End Time</Label>
                  <Input
                    type="time"
                    value={extractedData.end_time || ''}
                    onChange={(e) => updateExtractedField('end_time', e.target.value)}
                    className="rounded-sm border-border/50"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Location</Label>
                  <Input
                    value={extractedData.location || ''}
                    onChange={(e) => updateExtractedField('location', e.target.value)}
                    className="rounded-sm border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Category</Label>
                  <Select 
                    value={extractedData.category || 'other'} 
                    onValueChange={(v) => updateExtractedField('category', v)}
                  >
                    <SelectTrigger className="rounded-sm border-border/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                      <SelectItem value="networking">Networking Mixer</SelectItem>
                      <SelectItem value="webinar">Webinar</SelectItem>
                      <SelectItem value="seminar">Seminar</SelectItem>
                      <SelectItem value="training">Training Session</SelectItem>
                      <SelectItem value="panel">Panel Discussion</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Address</Label>
                <Input
                  value={extractedData.address || ''}
                  onChange={(e) => updateExtractedField('address', e.target.value)}
                  className="rounded-sm border-border/50"
                />
              </div>

              {extractedData.source_url && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Link2 className="w-4 h-4" />
                  <a 
                    href={extractedData.source_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-gold"
                  >
                    {extractedData.source_name || 'View Original'}
                  </a>
                </div>
              )}

              <div className="flex gap-4 pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  onClick={() => setExtractedData(null)}
                  className="flex-1"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="flex-1 bg-gold text-primary hover:bg-gold/90"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Import Event
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-4 bg-secondary/50 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Supported Platforms</p>
              <p>
                Our AI can extract event details from most event pages including 
                LinkedIn Events, Eventbrite, Meetup, and general event websites.
                You can review and edit the extracted data before importing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportEvent;
